import { ReactNode, useEffect, useState } from 'react'

import i18n from '@signavio/i18n'

import { FileSelect, LegacyHint, TextTile } from '@signavio/ui'

import { useApi } from '../../api'
import apiTypes from '../../apiTypes'

import { useCallbackWhenRequestFulfills } from '../../hooks'
import { Nullable } from '@signavio/effektif-commons'
import { FileT } from '../../../index'

const MAX_FILE_SIZE = 50 * 1000 * 1000

type FileUploadProps = {
  endpoint?: string
  onFileUploaded: (file: FileT) => void
  publicFileScope?: boolean
  small?: boolean
  title?: ReactNode
  uploadingMessage?: ReactNode
}

function FileUpload({
  endpoint,
  onFileUploaded,
  publicFileScope,
  small,
  title,
  uploadingMessage,
}: FileUploadProps) {
  const [fileTooLarge, setFileTooLarge] = useState(false)
  const [uploadFileRequest, uploadFile] = useApi(
    publicFileScope ? apiTypes.PUBLIC_FILE : apiTypes.FILE,
    {
      method: 'create',
      requestParams: {
        endpoint,
      },
    }
  )

  useCallbackWhenRequestFulfills(uploadFileRequest, () => {
    onFileUploaded(uploadFileRequest.value)
  })

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>

    if (fileTooLarge) {
      timeout = setTimeout(() => setFileTooLarge(false), 3000)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [fileTooLarge])

  if (uploadFileRequest.pending) {
    return uploadingMessage ? (
      <>{uploadingMessage}</>
    ) : (
      <TextTile transparent small={small}>
        <LegacyHint loading inline small={small}>
          {i18n('Uploading file...')}
        </LegacyHint>
      </TextTile>
    )
  }

  if (fileTooLarge) {
    return (
      <TextTile transparent small={small}>
        <LegacyHint inline danger small={small}>
          {i18n('The maximum file size is __size__MB', {
            size: MAX_FILE_SIZE / 1000 / 1000,
          })}
        </LegacyHint>
      </TextTile>
    )
  }

  if (uploadFileRequest.rejected) {
    return (
      <TextTile transparent small={small}>
        <LegacyHint inline danger small={small}>
          {uploadFileRequest.reason}
        </LegacyHint>
      </TextTile>
    )
  }

  return (
    <FileSelect
      small={small}
      onChange={(file: Nullable<File>) => {
        if (file == null) {
          return
        }

        if (file.size === undefined || file.size < MAX_FILE_SIZE) {
          uploadFile(file)
        } else {
          setFileTooLarge(true)
        }
      }}
    >
      {title || i18n('Upload document')}
    </FileSelect>
  )
}

export default FileUpload
